import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { auditLogsAPI, usersAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  List, 
  Clock,
  User,
  ShieldCheck
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const AuditLogsPage = () => {
  const { user } = useAuth();
  const [auditLogs, setAuditLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState('all');
  const [filterResource, setFilterResource] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [logsRes, usersRes] = await Promise.all([
        auditLogsAPI.getAll({}),
        usersAPI.getAll()
      ]);
      setAuditLogs(logsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (userId) => {
    const foundUser = users.find(u => u.id === userId);
    return foundUser?.name || 'Unknown User';
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'CREATE': return 'bg-green-100 text-green-800';
      case 'UPDATE': return 'bg-blue-100 text-blue-800';
      case 'DELETE': return 'bg-red-100 text-red-800';
      case 'LOGIN': return 'bg-yellow-100 text-yellow-800';
      case 'LOGOUT': return 'bg-gray-100 text-gray-800';
      case 'CHECK_IN': return 'bg-purple-100 text-purple-800';
      case 'CHECK_OUT': return 'bg-indigo-100 text-indigo-800';
      case 'AI_SUGGESTION': return 'bg-pink-100 text-pink-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredLogs = auditLogs.filter(log => {
    if (filterUser !== 'all' && log.user_id !== filterUser) return false;
    if (filterResource !== 'all' && log.resource_type !== filterResource) return false;
    return true;
  });

  const resourceTypes = [...new Set(auditLogs.map(log => log.resource_type))];

  if (user?.role !== 'supervisor') {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <ShieldCheck weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" />
        <h3 className="font-heading text-lg font-medium">Access Restricted</h3>
        <p className="text-muted-foreground text-sm mt-1">
          Only supervisors can view audit logs
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground flex items-center gap-2">
            <ShieldCheck weight="duotone" className="w-8 h-8 text-primary" />
            Audit Logs
          </h1>
          <p className="text-muted-foreground mt-1">
            HIPAA compliance activity tracking
          </p>
        </div>
        <div className="hipaa-badge">
          <ShieldCheck weight="duotone" className="w-4 h-4" />
          HIPAA Compliant
        </div>
      </div>

      {/* Filters */}
      <Card className="rounded-2xl border border-border">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Select value={filterUser} onValueChange={setFilterUser}>
                <SelectTrigger className="rounded-xl" data-testid="filter-user-select">
                  <SelectValue placeholder="Filter by user" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Select value={filterResource} onValueChange={setFilterResource}>
                <SelectTrigger className="rounded-xl" data-testid="filter-resource-select">
                  <SelectValue placeholder="Filter by resource" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Resources</SelectItem>
                  {resourceTypes.map(type => (
                    <SelectItem key={type} value={type}>{type.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Audit Logs List */}
      {filteredLogs.length === 0 ? (
        <Card className="rounded-2xl border border-border">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <List weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <h3 className="font-heading text-lg font-medium">No audit logs found</h3>
            <p className="text-muted-foreground text-sm mt-1">
              Activity logs will appear here
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-2xl border border-border" data-testid="audit-logs-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Activity Log</CardTitle>
            <CardDescription>
              {filteredLogs.length} entries
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {filteredLogs.map((log) => (
                <div 
                  key={log.id} 
                  className="p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors"
                  data-testid={`audit-log-${log.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <User weight="duotone" className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">
                          {getUserName(log.user_id)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {log.details || `${log.action} on ${log.resource_type}`}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className={`${getActionColor(log.action)} rounded-full text-xs`}>
                            {log.action}
                          </Badge>
                          <Badge variant="outline" className="rounded-full text-xs">
                            {log.resource_type}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock weight="duotone" className="w-3 h-3" />
                        {format(new Date(log.timestamp), 'MMM d, h:mm a')}
                      </p>
                      {log.ip_address && (
                        <p className="text-xs text-muted-foreground mt-1">
                          IP: {log.ip_address}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AuditLogsPage;
